import type { Channel } from "@ai-leasing-os/shared";
import { cn } from "@/lib/cn";

/**
 * Canonical brand colors for each transport. Matches `tailwind.config.ts`.
 * Used by ChannelBadge AND the conversation row border / filter chips.
 */
export const CHANNEL_COLORS: Record<
  Channel,
  { hex: string; soft: string; label: string }
> = {
  whatsapp:    { hex: "#25D366", soft: "#DCF8C6", label: "WhatsApp" },
  instagram:   { hex: "#E1306C", soft: "#FCE4EC", label: "Instagram" },
  messenger:   { hex: "#0084FF", soft: "#DBEAFE", label: "Messenger" },
  marketplace: { hex: "#0EA5A4", soft: "#CCFBF1", label: "Marketplace" },
};

/**
 * Inline SVG glyphs. Kept tiny - single-color, white when used on filled
 * brand-color backgrounds, currentColor when used as outline.
 */
function ChannelGlyph({ channel, className }: { channel: Channel; className?: string }) {
  switch (channel) {
    case "whatsapp":
      // Stylized speech-bubble with phone hint - keeps the WA "vibe" without
      // needing the full official logo (which we don't have rights to anyway).
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
          <path d="M12 2C6.5 2 2 6.4 2 11.8c0 1.9.5 3.7 1.5 5.3L2 22l5-1.5c1.6.9 3.4 1.3 5 1.3 5.5 0 10-4.4 10-9.9C22 6.4 17.5 2 12 2zm5.6 14c-.2.7-1.4 1.3-2 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-3-1.3-5-4.3-5.2-4.5-.1-.2-1.2-1.6-1.2-3 0-1.4.8-2.1 1-2.4.3-.3.6-.4.8-.4h.6c.2 0 .4 0 .7.5l1 2.3c.1.2.1.4 0 .6l-.4.5-.2.3c-.1.1-.2.3 0 .5.2.4.8 1.3 1.7 2 1.1 1 2 1.3 2.3 1.4.2.1.4 0 .5-.1l.6-.7c.2-.2.4-.2.6-.1l1.9 1c.3.1.5.2.5.4.1.2.1 1-.2 1.7z"/>
        </svg>
      );
    case "instagram":
      // Simplified IG camera icon.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      );
    case "messenger":
      // Simplified Messenger speech bubble.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
          <path d="M12 2C6.5 2 2 6.2 2 11.4c0 2.8 1.3 5.4 3.5 7.1V22l3.2-1.8c1 .3 2.1.5 3.3.5 5.5 0 10-4.2 10-9.3S17.5 2 12 2zm1 12.5l-2.5-2.6L5.7 14.5l5.3-5.6 2.6 2.6 4.7-2.6-5.3 5.6z"/>
        </svg>
      );
    case "marketplace":
      // Storefront / shopping bag.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
          <path d="M3 7l1.5-3h15L21 7M3 7v13a1 1 0 001 1h16a1 1 0 001-1V7M3 7h18M8 11a4 4 0 008 0" />
        </svg>
      );
  }
}

/**
 * ChannelBadge - filled circle with brand color + white glyph.
 *
 * `size="sm"` (24px) → list rows. `size="md"` (32px) → conversation header.
 */
export function ChannelBadge({
  channel,
  size = "sm",
  className,
}: {
  channel: Channel;
  size?: "sm" | "md";
  className?: string;
}) {
  const c = CHANNEL_COLORS[channel];
  const dim = size === "md" ? "h-8 w-8" : "h-6 w-6";
  const glyph = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <span
      title={c.label}
      style={{ background: c.hex, color: "#fff" }}
      className={cn(
        "inline-flex items-center justify-center rounded-full shadow-sm shrink-0",
        dim,
        className,
      )}
    >
      <ChannelGlyph channel={channel} className={glyph} />
    </span>
  );
}

/**
 * ChannelChip - pill button used by filter rows. Brand color when active,
 * neutral otherwise.
 */
export function ChannelChip({
  channel,
  active,
  onClick,
}: {
  channel: Channel;
  active: boolean;
  onClick: () => void;
}) {
  const c = CHANNEL_COLORS[channel];
  return (
    <button
      type="button"
      onClick={onClick}
      style={
        active
          ? { background: c.hex, color: "#fff", borderColor: c.hex }
          : { borderColor: "transparent" }
      }
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
        !active && "bg-slate-100 text-slate-700 hover:bg-slate-200",
      )}
    >
      <ChannelGlyph channel={channel} className="h-3 w-3" />
      {c.label}
    </button>
  );
}
